/* eslint-disable no-undef */

import React, { useState } from 'react';
import { Styles } from './style';
import { Link } from 'react-router-dom';
import BedIcon from '@assets/images/bed-ico.png';
import HomeIcon from '@assets/images/home-ico.png';
import GarageIcon from '@assets/images/garage-ico.png';
import ShowerIcon from '@assets/images/shower-ico.png';
import PriceFlag from '../PriceFlag';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { usePropertyStore } from '@stores/Property/Property';
import { IPropertyDetails } from '@stores/Property/types';
import PropertyDetailsCard from '@components/PropertyDetailsCard';

interface PropertyProps {
  id?: string;
  imageUrl: string;
  title: string;
  cod: string;
  size: string;
  rooms: string;
  shower: string;
  garage: string;
  propertyType: string;
  price: string;
  operationType: 'VENDA' | 'ALUGUEL';
  BottomTray?: React.ComponentType;
  suppressLink?: boolean;
  openModal?: boolean;
}

const clampMin = (value: string): string => {
  const num = parseFloat(value);
  return isNaN(num) ? value : String(Math.max(0, num));
};

const PropertyCard: React.FC<PropertyProps> = ({
  id,
  imageUrl,
  title,
  cod,
  size,
  rooms,
  shower,
  garage,
  propertyType,
  price,
  operationType,
  BottomTray,
  suppressLink = false,
  openModal = false
}) => {
  const [hover, setHover] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [propertyDetails, setPropertyDetails] =
    useState<IPropertyDetails | null>(null);
  const { fetchPrivatePropertyDetails } = usePropertyStore();

  const handleCardClick = async (e: React.MouseEvent) => {
    if (!openModal) return;
    e.preventDefault();
    e.stopPropagation();
    setModalOpen(true);
    if (!propertyDetails && id) {
      const data = await fetchPrivatePropertyDetails(parseInt(id));
      setPropertyDetails(data);
    }
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    setModalOpen(false);
  };

  const LinkComponent = suppressLink || openModal ? 'div' : Link;

  return (
    <>
      <LinkComponent to={`/imoveis/${id}`} style={{ textDecoration: 'none' }}>
        <Styles.CardContainer
          onClick={handleCardClick}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}>
          <Styles.ImageContainer>
            {imageUrl ? (
              <Styles.Image src={imageUrl} alt={title} />
            ) : (
              <Styles.EmptyImage>Sem Imagem de Capa</Styles.EmptyImage>
            )}
            <Styles.PropertyPosition>
              <PriceFlag
                price={price}
                category={propertyType}
                priceFontSize="18px"
                categoryFontSize="12px"
                categoryMarginTop="-2px"
              />
            </Styles.PropertyPosition>
            <Styles.OperationTag isAluguel={operationType === 'ALUGUEL'}>
              {operationType}
            </Styles.OperationTag>
          </Styles.ImageContainer>
          {!!BottomTray && BottomTray !== null && BottomTray !== undefined ? (
            <Styles.Footer hover={hover}>
              <BottomTray />
            </Styles.Footer>
          ) : (
            <Styles.Footer hover={hover}>
              <Styles.Icons>
                <Styles.IconBox>
                  <Styles.IconImg src={HomeIcon} />
                  <Styles.Info>{clampMin(size)} m²</Styles.Info>
                </Styles.IconBox>

                <Styles.IconBox>
                  <Styles.IconImg src={BedIcon} />
                  <Styles.Info>{clampMin(rooms)}</Styles.Info>
                </Styles.IconBox>

                <Styles.IconBox>
                  <Styles.IconImg src={ShowerIcon} />
                  <Styles.Info>{clampMin(shower)}</Styles.Info>
                </Styles.IconBox>

                <Styles.IconBox>
                  <Styles.IconImg src={GarageIcon} />
                  <Styles.Info>{clampMin(garage)}</Styles.Info>
                </Styles.IconBox>
              </Styles.Icons>
              <Styles.FooterTextContainer>
                <Styles.Text>{title}</Styles.Text>
                <Styles.CodText>Cod: {cod}</Styles.CodText>
              </Styles.FooterTextContainer>
            </Styles.Footer>
          )}
        </Styles.CardContainer>
      </LinkComponent>

      {openModal && (
        <Dialog
          open={modalOpen}
          onClose={handleCloseModal}
          maxWidth={false}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ sx: { width: 'fit-content', maxWidth: '95vw' } }}>
          <DialogContent sx={{ padding: 0, position: 'relative' }}>
            <IconButton
              onClick={handleCloseModal}
              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
              <CloseIcon />
            </IconButton>
            <PropertyDetailsCard key={id} property={propertyDetails} />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default PropertyCard;
